import styled, { css } from 'styled-components';

const buttonVariants = {
  primary: css`
    background: ${p => p.theme.colors.gradient.primary};
    color: ${p => p.theme.colors.light};
    border: 0;
    background-size: 200% 200%;
    box-shadow: ${p => p.theme.shadows.md};

    &:hover {
      /* Hover goes blue but keep text legible (white) */
      background: ${p => p.theme.colors.gradient.secondary};
      color: ${p => p.theme.colors.light};
      transform: translateY(-2px);
      box-shadow: ${p => p.theme.shadows['2xl']};
    }

    &:active {
      transform: translateY(0);
      box-shadow: ${p => p.theme.shadows.lg};
    }
  `,
  secondary: css`
    background: transparent;
    color: ${p => p.theme.colors.primary};
    border: 2px solid ${p => p.theme.colors.primary};

    &:hover {
      background: ${p => p.theme.colors.gradient.primary};
      color: ${p => p.theme.colors.light};
      border-color: transparent;
      transform: translateY(-2px);
      box-shadow: ${p => p.theme.shadows.md};
    }
  `,
  outline: css`
    background: transparent;
    color: ${p => p.theme.colors.dark};
    border: 2px solid ${p => p.theme.colors.gray[300]};

    &:hover {
      background: rgba(255, 68, 68, 0.08); /* subtle primary tint */
      color: ${p => p.theme.colors.dark};
      border-color: ${p => p.theme.colors.primary};
    }
  `,
  ghost: css`
    background: transparent;
    color: ${p => p.theme.colors.primary};
    border: 2px solid transparent;

    &:hover {
      background: ${p => p.theme.colors.gray[100]};
      color: ${p => p.theme.colors.primary};
    }
  `
};

const buttonSizes = {
  small: css`
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
    font-size: ${props => props.theme.fontSizes.sm};
  `,
  medium: css`
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
    font-size: ${props => props.theme.fontSizes.base};
  `,
  large: css`
    padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing['2xl']};
    font-size: ${props => props.theme.fontSizes.lg};
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
      font-size: ${props => props.theme.fontSizes.base};
    }
  `
};

export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  font-family: ${props => props.theme.fonts.primary};
  font-weight: ${props => props.theme.fontWeights.semibold};
  border-radius: ${props => props.theme.borderRadius.full};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  white-space: nowrap;
  position: relative;
  overflow: hidden;

  /* Icons follow text color */
  svg { 
    color: currentColor;
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
  
  ${props => buttonVariants[props.variant || 'primary']}
  ${props => buttonSizes[props.size || 'medium']}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }

  ${props => props.fullWidth && css`
    width: 100%;
  `}
`;

export default Button;